import produce from "immer";
import { createAction, createAsyncAction } from "typesafe-actions";
import { ActionType, createReducer } from "typesafe-actions";
import { Booking, APICallStatus, HoldBooking, Comment } from "../../../Types/UnitFeatureTypes";

interface BookingRequestState {
  request: {
    loading: boolean;
    bookingCreateStatus: APICallStatus;
    bookingUpdateStatus: APICallStatus;
    bookingHoldStatus: APICallStatus;
    bookingUpdateHoldStatus: APICallStatus;
    approvalStatus: APICallStatus;
    rejectStatus: APICallStatus;
    cancelStatus: APICallStatus;
    bookingHoldError: string | null;
    error: string | null;
    bookingHoldId: number | null;
  };
}

export enum Types {
  CREATE_BOOKING_REQUEST = 'CREATE_BOOKING_REQUEST',
  CREATE_BOOKING_SUCCESS = 'CREATE_BOOKING_SUCCESS',
  CREATE_BOOKING_FAILURE = 'CREATE_BOOKING_FAILURE',
  CREATE_BOOKING_HOLD_REQUEST = 'CREATE_BOOKING_HOLD_REQUEST',
  CREATE_BOOKING_HOLD_SUCCESS = 'CREATE_BOOKING_HOLD_SUCCESS',
  CREATE_BOOKING_HOLD_FAILURE = 'CREATE_BOOKING_HOLD_FAILURE',
  UPDATE_BOOKING_REQUEST = 'UPDATE_BOOKING_REQUEST',
  UPDATE_BOOKING_SUCCESS = 'UPDATE_BOOKING_SUCCESS',
  UPDATE_BOOKING_FAILURE = 'UPDATE_BOOKING_FAILURE',
  UPDATE_BOOKING_HOLD_REQUEST = 'UPDATE_BOOKING_HOLD_REQUEST',
  UPDATE_BOOKING_HOLD_SUCCESS = 'UPDATE_BOOKING_HOLD_SUCCESS',
  UPDATE_BOOKING_HOLD_FAILURE = 'UPDATE_BOOKING_HOLD_FAILURE',
  APPROVE_BOOKING_REQUEST = 'APPROVE_BOOKING_REQUEST',
  APPROVE_BOOKING_SUCCESS = 'APPROVE_BOOKING_SUCCESS',
  APPROVE_BOOKING_FAILURE = 'APPROVE_BOOKING_FAILURE',
  REJECT_BOOKING_REQUEST = 'REJECT_BOOKING_REQUEST',
  REJECT_BOOKING_SUCCESS = 'REJECT_BOOKING_SUCCESS',
  REJECT_BOOKING_FAILURE = 'REJECT_BOOKING_FAILURE',
  CREATE_BOOKING_COMMENT_REQUEST = 'CREATE_BOOKING_COMMENT_REQUEST',
  CREATE_BOOKING_COMMENT_SUCCESS = 'CREATE_BOOKING_COMMENT_SUCCESS',
  CREATE_BOOKING_COMMENT_FAILURE = 'CREATE_BOOKING_COMMENT_FAILURE',
  DELETE_BOOKING_COMMENT_REQUEST = 'DELETE_BOOKING_COMMENT_REQUEST',
  DELETE_BOOKING_COMMENT_SUCCESS = 'DELETE_BOOKING_COMMENT_SUCCESS',
  DELETE_BOOKING_COMMENT_FAILURE = 'DELETE_BOOKING_COMMENT_FAILURE',
  CANCEL_BOOKING_REQUEST = 'CANCEL_BOOKING_REQUEST',
  CANCEL_BOOKING_SUCCESS = 'CANCEL_BOOKING_SUCCESS',
  CANCEL_BOOKING_FAILURE = 'CANCEL_BOOKING_FAILURE',

  RESET_STATUS_REQUEST = 'RESET_STATUS_REQUEST',
}

const createBookingHoldRequestAction = createAsyncAction(
  Types.CREATE_BOOKING_HOLD_REQUEST,
  Types.CREATE_BOOKING_HOLD_SUCCESS,
  Types.CREATE_BOOKING_HOLD_FAILURE,
)
<
  { bookingData: HoldBooking },
  { holdId: number },
  { message: string }
>();

const createBookingRequestAction = createAsyncAction(
  Types.CREATE_BOOKING_REQUEST,
  Types.CREATE_BOOKING_SUCCESS,
  Types.CREATE_BOOKING_FAILURE,
)
<
  { id: number },
  { success: boolean },
  { message: string }
>();

const updateBookingRequestAction = createAsyncAction(
  Types.UPDATE_BOOKING_REQUEST,
  Types.UPDATE_BOOKING_SUCCESS,
  Types.UPDATE_BOOKING_FAILURE,
)
<
  { bookingData: {
      id: number,
      slots?: Date[],
      amenity_id: number,
      booking_start: Date,
      booking_end?: Date,
  }},
  void,
  { message: string }
>();

const updateBookingHoldRequestAction = createAsyncAction(
  Types.UPDATE_BOOKING_HOLD_REQUEST,
  Types.UPDATE_BOOKING_HOLD_SUCCESS,
  Types.UPDATE_BOOKING_HOLD_FAILURE,
)
<
  { bookingData: {
    id: number,
    slots?: Date[],
    amenity_id: number,
    booking_start: Date,
    booking_end?: Date,
  } },
  void,
  { message: string }
>();

const approveBookingRequestAction = createAsyncAction(
  Types.APPROVE_BOOKING_REQUEST,
  Types.APPROVE_BOOKING_SUCCESS,
  Types.APPROVE_BOOKING_FAILURE,
)
<
  { booking_id: number },
  void,
  { message: string }
>();

const rejectBookingRequestAction = createAsyncAction(
  Types.REJECT_BOOKING_REQUEST,
  Types.REJECT_BOOKING_SUCCESS,
  Types.REJECT_BOOKING_FAILURE,
)<
  { booking_id: number },
  void,
  { message: string }
>();

const createBookingCommentRequestAction = createAsyncAction(
  Types.CREATE_BOOKING_COMMENT_REQUEST,
  Types.CREATE_BOOKING_COMMENT_SUCCESS,
  Types.CREATE_BOOKING_COMMENT_FAILURE
)<
  { comment: Comment },
  void,
  { message: string }
>();

const deleteBookingCommentRequestAction = createAsyncAction(
  Types.DELETE_BOOKING_COMMENT_REQUEST,
  Types.DELETE_BOOKING_COMMENT_SUCCESS,
  Types.DELETE_BOOKING_COMMENT_FAILURE
)<
  { commentId: number },
  void,
  { message: string }
>();

const cancelBookingRequestAction = createAsyncAction(
  Types.CANCEL_BOOKING_REQUEST,
  Types.CANCEL_BOOKING_SUCCESS,
  Types.CANCEL_BOOKING_FAILURE
)<
  { booking_id: number },
  void,
  { message: string }
>();

const resetStatusRequestAction = createAction(Types.RESET_STATUS_REQUEST)();

export const Actions = { 
  createBookingRequestAction,
  updateBookingRequestAction,
  updateBookingHoldRequestAction,
  createBookingHoldRequestAction,
  approveBookingRequestAction,
  rejectBookingRequestAction,
  createBookingCommentRequestAction,
  deleteBookingCommentRequestAction,
  cancelBookingRequestAction,
  resetStatusRequestAction
};

export const initialState: BookingRequestState = {
  request: {
    loading: false,
    error: null as string | null,
    bookingHoldError: null as string | null,
    bookingHoldStatus: APICallStatus.PENDING,
    bookingCreateStatus: APICallStatus.PENDING,
    bookingUpdateStatus: APICallStatus.PENDING,
    bookingUpdateHoldStatus: APICallStatus.PENDING,
    approvalStatus: APICallStatus.PENDING,
    rejectStatus: APICallStatus.PENDING,
    cancelStatus: APICallStatus.PENDING,
    bookingHoldId: null,
  }
};

export type BookingRequestInfoState = typeof initialState;

const resetStatusRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.bookingCreateStatus = APICallStatus.PENDING;
    draft.request.bookingUpdateStatus = APICallStatus.PENDING;
    draft.request.bookingUpdateHoldStatus = APICallStatus.PENDING;
    draft.request.error = null;
    draft.request.bookingHoldError = null;
    draft.request.bookingHoldStatus = APICallStatus.PENDING;
    draft.request.bookingHoldId = null;
    draft.request.approvalStatus = APICallStatus.PENDING;
    draft.request.rejectStatus = APICallStatus.PENDING;
    draft.request.cancelStatus = APICallStatus.PENDING;
  });

const createBookingHoldRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.bookingHoldStatus = APICallStatus.PENDING;
    draft.request.bookingHoldError = null;
    draft.request.error = null;
    draft.request.bookingHoldId = null;
  });

const createBookingHoldSuccess = (
  state = initialState,
  { payload }: ActionType<typeof Actions.createBookingHoldRequestAction.success>
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.bookingHoldStatus = APICallStatus.SUCCESS;
    draft.request.bookingHoldError = null;
    draft.request.loading = false;
    draft.request.bookingHoldId = payload.holdId;
  });

const createBookingHoldFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.createBookingHoldRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.bookingHoldStatus = APICallStatus.FAILURE;
    draft.request.bookingHoldError = payload.message;
    draft.request.error = payload.message;
  });

const createBookingRequestRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.bookingCreateStatus = APICallStatus.PENDING;
    draft.request.error = null;

    return draft;
  });

const createBookingRequestSuccess = (
  state = initialState,
  { payload }: ActionType<typeof Actions.createBookingRequestAction.success>
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.bookingCreateStatus = APICallStatus.SUCCESS;
    draft.request.loading = false;

    return draft;
  });

const createBookingRequestFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.createBookingRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.bookingCreateStatus = APICallStatus.FAILURE;
    draft.request.error = payload.message;

    return draft;
  });

const updatedBookingRequestRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;
    draft.request.bookingUpdateStatus = APICallStatus.PENDING;

    return draft;
  });

const updatedBookingRequestSuccess = (
  state = initialState,
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.loading = false;
    draft.request.bookingUpdateStatus = APICallStatus.SUCCESS;

    return draft;
  });

const updatedBookingRequestFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.updateBookingRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = payload.message;
    draft.request.bookingUpdateStatus = APICallStatus.FAILURE;

    return draft;
  });

const updatedBookingHoldRequestRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;
    draft.request.bookingUpdateHoldStatus = APICallStatus.PENDING;
  });

const updatedBookingHoldRequestSuccess = (
  state = initialState,
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.loading = false;
    draft.request.bookingUpdateHoldStatus = APICallStatus.SUCCESS;
  }
);

const updatedBookingHoldRequestFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.updateBookingHoldRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = payload.message;
    draft.request.bookingUpdateHoldStatus = APICallStatus.FAILURE;
  });

const approveBookingRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;
    draft.request.approvalStatus = APICallStatus.PENDING;

    return draft;
  });

const approveBookingSuccess = (
  state = initialState,
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.loading = false;
    draft.request.approvalStatus = APICallStatus.SUCCESS;
  });

const approveBookingFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.approveBookingRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = payload.message;
    draft.request.approvalStatus = APICallStatus.FAILURE;
  });

const rejectBookingRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;
    draft.request.rejectStatus = APICallStatus.PENDING;

    return draft;
  }
);

const rejectBookingSuccess = (
  state = initialState,
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.loading = false;
    draft.request.rejectStatus = APICallStatus.SUCCESS;
  });

const rejectBookingFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.rejectBookingRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = payload.message;
    draft.request.rejectStatus = APICallStatus.FAILURE;
  });

const createBookingCommentRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;

    return draft;
  });

const createBookingCommentSuccess = (
  state = initialState,
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.loading = false;

    return draft;
  });

const createBookingCommentFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.createBookingCommentRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = payload.message;

    return draft;
  });

const deleteBookingCommentRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;

    return draft;
  });

const deleteBookingCommentSuccess = (
  state = initialState,
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.loading = false;

    return draft;
  });

const deleteBookingCommentFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.deleteBookingCommentRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = payload.message;

    return draft;
  });

const cancelBookingRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;
    draft.request.cancelStatus = APICallStatus.PENDING;
  });

const cancelBookingSuccess = (
  state = initialState,
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.loading = false;
    draft.request.cancelStatus = APICallStatus.SUCCESS;
  });

const cancelBookingFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.cancelBookingRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = payload.message;
    draft.request.cancelStatus = APICallStatus.FAILURE;
  });

export default createReducer<typeof initialState, ActionType<typeof Actions>>(
  initialState
)
  .handleAction(Actions.createBookingRequestAction.request, createBookingRequestRequest)
  .handleAction(Actions.createBookingRequestAction.success, createBookingRequestSuccess)
  .handleAction(Actions.createBookingRequestAction.failure, createBookingRequestFailure)
  .handleAction(Actions.updateBookingRequestAction.request, updatedBookingRequestRequest)
  .handleAction(Actions.updateBookingRequestAction.success, updatedBookingRequestSuccess)
  .handleAction(Actions.updateBookingRequestAction.failure, updatedBookingRequestFailure)
  .handleAction(Actions.updateBookingHoldRequestAction.request, updatedBookingHoldRequestRequest)
  .handleAction(Actions.updateBookingHoldRequestAction.success, updatedBookingHoldRequestSuccess)
  .handleAction(Actions.updateBookingHoldRequestAction.failure, updatedBookingHoldRequestFailure)
  .handleAction(Actions.createBookingHoldRequestAction.request, createBookingHoldRequest)
  .handleAction(Actions.createBookingHoldRequestAction.success, createBookingHoldSuccess)
  .handleAction(Actions.createBookingHoldRequestAction.failure, createBookingHoldFailure)
  .handleAction(Actions.approveBookingRequestAction.request, approveBookingRequest)
  .handleAction(Actions.approveBookingRequestAction.success, approveBookingSuccess)
  .handleAction(Actions.approveBookingRequestAction.failure, approveBookingFailure)
  .handleAction(Actions.rejectBookingRequestAction.request, rejectBookingRequest)
  .handleAction(Actions.rejectBookingRequestAction.success, rejectBookingSuccess)
  .handleAction(Actions.rejectBookingRequestAction.failure, rejectBookingFailure)
  .handleAction(Actions.createBookingCommentRequestAction.request, createBookingCommentRequest)
  .handleAction(Actions.createBookingCommentRequestAction.success, createBookingCommentSuccess)
  .handleAction(Actions.createBookingCommentRequestAction.failure, createBookingCommentFailure)
  .handleAction(Actions.deleteBookingCommentRequestAction.request, deleteBookingCommentRequest)
  .handleAction(Actions.deleteBookingCommentRequestAction.success, deleteBookingCommentSuccess)
  .handleAction(Actions.deleteBookingCommentRequestAction.failure, deleteBookingCommentFailure)
  .handleAction(Actions.cancelBookingRequestAction.request, cancelBookingRequest)
  .handleAction(Actions.cancelBookingRequestAction.success, cancelBookingSuccess)
  .handleAction(Actions.cancelBookingRequestAction.failure, cancelBookingFailure)
  .handleAction(Actions.resetStatusRequestAction, resetStatusRequest);
  